import type { JSONPatchOperation } from 'immutable-json-patch'
import type { StructuredValue } from './structured'
import { fieldsOf, pointerOf, valueAt, type StructuredField } from './groups'

type RecordValue = { [key: string]: StructuredValue }
export const REQUIREMENTS = [
  { value: 'required_fields', label: '必填' }, { value: 'recommended_fields', label: '推荐' },
  { value: 'optional_fields', label: '可选' },
] as const
export type Requirement = typeof REQUIREMENTS[number]['value']
export type FieldRow = { key: string; requirement: Requirement | null; requirementEditable: boolean; cells: StructuredField[]; details: StructuredField[] }
const MAIN = new Set(['label', 'business_meaning'])
const recordOf = (value: StructuredValue | undefined): value is RecordValue =>
  value !== null && typeof value === 'object' && !Array.isArray(value)

const requirementsOf = (root: RecordValue, key: string) => REQUIREMENTS.filter(({ value }) =>
  Array.isArray(root[value]) && root[value].includes(key))

const editableRequirement = (root: RecordValue, key: string) => {
  const lists = REQUIREMENTS.map(({ value }) => root[value])
  return lists.every((items) => Array.isArray(items) && items.every((item) => typeof item === 'string'))
    && lists.flatMap((items) => Array.isArray(items) ? items : []).filter((item) => item === key).length === 1
}

/** Projection bindings retain native identities and distinct policy scopes; they never normalize the document. */
export const fieldRows = (value: StructuredValue): FieldRow[] | null => {
  if (!recordOf(value) || !Array.isArray(value.field_definitions)) return null
  const definitions = value.field_definitions
  if (!definitions.every((item) => recordOf(item) && typeof item.key === 'string' && item.key.length > 0)) return null
  const keys = definitions.map((item) => (item as RecordValue).key as string)
  if (new Set(keys).size !== keys.length) return null
  const semantics = recordOf(value.field_semantics) ? value.field_semantics : {}
  return definitions.map((definition, index) => {
    const key = keys[index]
    const declared = fieldsOf(definition, ['field_definitions', index]).filter((field) => field.path.slice(-1)[0] !== 'key')
    const meaning = recordOf(semantics[key]) ? fieldsOf(semantics[key], ['field_semantics', key]) : []
    const bindings = [...declared, ...meaning]
    const requirements = requirementsOf(value, key)
    return { key, requirement: requirements.length === 1 ? requirements[0].value : null, requirementEditable: editableRequirement(value, key),
      cells: bindings.filter((field) => MAIN.has(String(field.path.slice(-1)[0]))),
      details: bindings.filter((field) => !MAIN.has(String(field.path.slice(-1)[0]))) }
  })
}

/** A row event must still belong to the same native key, source position and value at commit time. */
export const fieldChanges = (root: StructuredValue, key: string, field: StructuredField, value: StructuredValue): JSONPatchOperation[] => {
  const row = fieldRows(root)?.find((item) => item.key === key)
  const binding = [...(row?.cells ?? []), ...(row?.details ?? [])].find((item) => item.id === field.id)
  if (!binding || JSON.stringify(binding.value) !== JSON.stringify(field.value)) throw new Error('字段已变化，请重新读取')
  return [{ op: 'test', path: '/field_definitions/' + fieldRows(root)!.findIndex((item) => item.key === key) + '/key', value: key },
    { op: 'test', path: pointerOf(field.path), value: field.value }, { op: 'replace', path: pointerOf(field.path), value }]
}

/** This explicit command changes base-list membership only, never definition/module/conditional requirements. */
export const requirementChanges = (root: StructuredValue, key: string, target: Requirement): JSONPatchOperation[] => {
  if (!recordOf(root) || !fieldRows(root)?.some((row) => row.key === key)) throw new Error('字段已变化，请重新读取')
  if (!editableRequirement(root, key)) throw new Error('填写要求需使用高级编辑')
  const memberships = requirementsOf(root, key)
  const current = memberships[0]?.value
  if (memberships.length !== 1 || !current) throw new Error('填写要求不唯一，请使用高级编辑')
  if (current === target) return []
  if (!REQUIREMENTS.some(({ value }) => value === target)) throw new Error('填写要求无效')
  const previous = valueAt(root, [current]) as string[]
  return [
    { op: 'test', path: '/' + current, value: previous },
    { op: 'test', path: '/' + target, value: root[target] },
    { op: 'remove', path: '/' + current + '/' + previous.indexOf(key) },
    { op: 'add', path: '/' + target + '/-', value: key },
  ]
}
